import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalPercentage",
    "remainingPercentage", 
    "status",
    "progressBar",
    "projectPercentage",
    "completeButton",
    "helpText"
  ]

  connect() {
    this.updateTotals()
    // Store initial values for each slider
    this.initializeSliderValues()
  }
  
  getCurrentTotal() {
    const sliders = this.element.querySelectorAll('input[type="range"]')
    let total = 0
    sliders.forEach(slider => {
      total += parseInt(slider.value) || 0
    })
    return total
  }
  
  updateProjectPercentageDisplay(projectId, percentage) {
    // Find the specific percentage display for this project only
    const projectContainer = this.element.querySelector(`[data-project-id="${projectId}"]`)
    if (projectContainer) {
      const percentageDisplay = projectContainer.querySelector('[data-effort-allocation-target="projectPercentage"]')
      if (percentageDisplay) {
        // Use a more careful update approach to avoid DOM issues
        // Only update if the value has actually changed
        const currentText = percentageDisplay.textContent
        const newText = `${percentage}%`
        if (currentText !== newText) {
          // Add extra protection when dealing with critical updates (like when hitting 100%)
          const currentTotal = this.getCurrentTotal()
          if (currentTotal === 100) {
            // Use a longer delay when at 100% to avoid conflicts with status updates
            setTimeout(() => {
              if (percentageDisplay.textContent !== newText) {
                percentageDisplay.textContent = newText
              }
            }, 20)
          } else {
            requestAnimationFrame(() => {
              percentageDisplay.textContent = newText
            })
          }
        }
      }
    }
  }
  
  initializeSliderValues() {
    const sliders = this.element.querySelectorAll('input[type="range"]')
    sliders.forEach(slider => {
      slider.dataset.oldValue = slider.value
    })
  }

  updateAllocations(event) {
    const changedSlider = event.target
    const projectId = changedSlider.dataset.projectId
    const newPercentage = parseInt(changedSlider.value)
    const oldPercentage = parseInt(changedSlider.dataset.oldValue || 0)
    
    // Store the new value as the old value for next time
    changedSlider.dataset.oldValue = newPercentage.toString()
    
    // Update the project percentage display with more targeted approach
    this.updateProjectPercentageDisplay(projectId, newPercentage)
    
    // Check if we would go over 100% and redistribute only if needed
    const currentTotal = this.getCurrentTotal()
    if (currentTotal > 100) {
      const excess = currentTotal - 100
      this.redistributeFromOtherSliders(projectId, excess)
    }
    
    // Update totals
    this.updateTotals()
  }
  
  redistributeFromOtherSliders(excludeProjectId, amountToRedistribute) {
    const otherSliders = Array.from(this.element.querySelectorAll('input[type="range"]'))
      .filter(slider => slider.dataset.projectId !== excludeProjectId)
      .filter(slider => parseInt(slider.value) > 0) // Only consider sliders with values > 0
    
    if (otherSliders.length === 0 || amountToRedistribute === 0) {
      return
    }
    
    let remainingToRedistribute = amountToRedistribute
    
    // Distribute the excess evenly across all other projects
    // Use multiple passes to ensure fair distribution
    while (remainingToRedistribute > 0 && otherSliders.some(s => parseInt(s.value) > 0)) {
      const availableSliders = otherSliders.filter(s => parseInt(s.value) > 0)
      const reductionPerSlider = Math.max(1, Math.ceil(remainingToRedistribute / availableSliders.length))
      
      availableSliders.forEach(slider => {
        if (remainingToRedistribute <= 0) return
        
        const currentValue = parseInt(slider.value)
        const reduction = Math.min(currentValue, reductionPerSlider, remainingToRedistribute)
        
        const newValue = currentValue - reduction
        slider.value = newValue
        slider.dataset.oldValue = newValue.toString()
        remainingToRedistribute -= reduction
        
        // Update the percentage display for this slider
        this.updateProjectPercentageDisplay(slider.dataset.projectId, newValue)
      })
    }
  }

  updateTotals() {
    // Calculate total percentage from all sliders
    const sliders = this.element.querySelectorAll('input[type="range"]')
    let total = 0
    
    sliders.forEach(slider => {
      total += parseInt(slider.value) || 0
    })
    
    const remaining = 100 - total
    
    // Use requestAnimationFrame to batch DOM updates and prevent interference
    requestAnimationFrame(() => {
      // Update displays
      this.totalPercentageTarget.textContent = `${total}%`
      this.remainingPercentageTarget.textContent = `${remaining}%`
      this.progressBarTarget.value = total
      
      // Update status
      let statusText = ""
      let statusClass = ""
      
      if (total === 100) {
        statusText = '<span class="badge badge-success badge-sm">Complete</span>'
        statusClass = "progress-success"
      } else if (total > 100) {
        statusText = '<span class="badge badge-error badge-sm">Over 100%</span>'
        statusClass = "progress-error"
      } else {
        statusText = '<span class="badge badge-warning badge-sm">Incomplete</span>'
        statusClass = "progress-warning"
      }
      
      this.statusTarget.innerHTML = statusText
      
      // Update progress bar color
      this.progressBarTarget.className = `progress ${statusClass} w-full h-2`
      
      // Update the complete button state with a slight delay to prevent interference
      setTimeout(() => this.updateCompleteButton(total), 10)
    })
  }
  
  updateCompleteButton(total) {
    if (!this.hasCompleteButtonTarget || !this.hasHelpTextTarget) return
    
    if (total === 100) {
      this.completeButtonTarget.disabled = false
      this.helpTextTarget.innerHTML = '&nbsp;' // Non-breaking space to prevent layout shift
    } else {
      this.completeButtonTarget.disabled = true
      this.helpTextTarget.textContent = 'Must total 100% to submit'
    }
  }
  
  toggleNotes(event) {
    const button = event.currentTarget
    const projectId = button.dataset.projectId
    const notesSection = this.element.querySelector(`[data-notes-section="${projectId}"]`)
    const icon = button.querySelector('iconify-icon')
    
    if (notesSection.classList.contains('hidden')) {
      notesSection.classList.remove('hidden')
      icon.setAttribute('icon', 'ph:chevron-up')
      button.setAttribute('aria-expanded', 'true')
    } else {
      notesSection.classList.add('hidden')
      icon.setAttribute('icon', 'ph:chevron-down')
      button.setAttribute('aria-expanded', 'false')
    }
  }
} 